<template>
  <div class="map" :key="updateCardKey">
    <div v-if="!selectedMap" class="no map-card">
      <span>{{ $t("SelectMap") }}</span>
    </div>
    <div v-if="!!selectedMap" class="map-info-container">
      <object-main-info
        :fields="fields"
        :identifier="cardObjectIdentifier"
        :identifier-label="$t('Name')"
        :editing-data="editingData"
        :show-edit-button="
          !creatingMap && userHasPermission('consumable_update')
        "
        :card-label="cardLabel"
        :height="cardHeight"
        :expand-card="editingData"
        :submit-button-label="submitButtonLabel"
        :required-fields-filled="requiredFieldsAreFilled"
        showEditActionButtons
        @toggle-edit="toggleEdit"
        @save-object="saveObject"
        @update-object="updateMap"
        @cancel-edit="discardChangesDialogIsOpen = true"
      >
      </object-main-info>
    </div>
    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="var(--app-primary-base)"
      style="position: absolute"
    />
    <dialog-choice
      :isOpen="saveChangesDialogIsOpen"
      :title="$t('SaveChanges')"
      :content="$t('SaveChangesContent')"
      @mainAction="saveObject"
      :mainActionLabel="$t('Confirm')"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="saveChangesDialogIsOpen = false"
      icon="mdi-content-save"
      iconColor="var(--app-primary-base)"
    />
    <dialog-choice
      :isOpen="discardChangesDialogIsOpen"
      :title="$t('DiscardChanges')"
      :content="$t('ClusterCreationConfirmation')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="cancelEdit"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="discardChangesDialogIsOpen = false"
      icon="mdi-delete"
      iconColor="var(--app-primary-base)"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { bus } from "@/main.js";
import _ from "lodash";
import utils from "@/utils";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { objectManagementMixin } from "@/mixins/objectManagementMixin";
import LoadingSpinner from "vue-loading-overlay";
import ObjectMainInfo from "@/newComponents/_Organisms/ObjectMainInfo";
import DialogContent from "@/newComponents/_Molecules/DialogContent.vue";
import DialogChoice from "@/newComponents/_Molecules/DialogChoice.vue";
import CustomButton from "@/newComponents/_Atoms/CustomButton";
import ThirdPartyDialog from "@/newComponents/Maps/ThirdPartyDialog.vue";
import { activitiesTasksDataMixin } from "@/mixins/field/activitiesTasksDataMixin";
export default {
  name: "Map",

  components: {
    LoadingSpinner,
    DialogChoice,
    ObjectMainInfo,
    CustomButton,
    DialogContent,
    ThirdPartyDialog,
  },

  mixins: [
    checkPermissionsMixin,
    objectManagementMixin,
    activitiesTasksDataMixin,
  ],

  props: {
    selectedMap: {
      type: Object,
    },
  },

  data() {
    return {
      dialog: true,
      loading: false,
      map: null,
      editingData: false,
      originalMap: {},
      specificRequiredFieldsAreFilled: false,
      dataToSend: {},
      actionToExecute: "UPDATE_SELECTED_MAP",
      submitButtonLabel: "Save",
      cardLabel: "EditMap",
      saveChangesDialogIsOpen: false,
      discardChangesDialogIsOpen: false,
      buttonStyling: {
        padding: "0px",
        "drop-shadow": "0.85px 0.85px 1.75px rgba(0, 0, 0, 0.25)",
        "border-radius": "4px",
      },

      topCardHeight: {
        style: {
          maxHeight: "100%",
          height: "auto",
        },
      },

      updateCardKey: 0,
    };
  },

  watch: {
    selectedMap(value) {
      if (value !== null && Object.keys(value).length === 0) {
        this.setCreateMapVariables();
      } else if (!!value) {
        this.setEditMapVariables();
      } else if (value === null) {
        this.clearVariables();
      }
    },

    editingData() {
      bus.$emit("toggle-create-edit-flag")
    },
  },

  computed: {
    requiredFieldsAreFilled() {
      return this.map
        ? !!this.map.code &&
            !!this.map.index &&
            !!this.map.url &&
            this.map.active !== null &&
            this.map.locked !== null
        : false;
    },

    creatingMap() {
      return this.actionToExecute === "createConsumables";
    },

    fields() {
      let fields = [];
      if (this.map !== null) {
        if (!this.editingData) {
          fields.splice(0, 0, {
            icon: "mdi-power",
            label: this.$i18n.t("Active"),
            value: this.editingData
              ? this.map.active
                ? "True"
                : "False"
              : this.map.active
              ? this.$i18n.t("Yes")
              : this.$i18n.t("No"),
            varName: "active",
            inputType: "radio",
            disabled: false,
            required: false,
            radioOptions: [
              { value: "True", label: this.$i18n.t("Yes") },
              { value: "False", label: this.$i18n.t("No") },
            ],
            size: "100%",
          });
          fields.splice(1, 0, {
            icon: "mdi-format-list-bulleted",
            label: this.$i18n.t("Index"),
            value: this.map.index,
            varName: "index",
            inputType: "int_pos",
            disabled: false,
            min: 0,
            required: true,
            size: "100%",
          });
          fields.splice(2, 0, {
            icon: "subject",
            label: this.$i18n.t("Description"),
            value: this.map.description,
            varName: "description",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(3, 0, {
            icon: "mdi-calendar",
            label: this.$i18n.t("UpdateDate"),
            value: this.map.updated_at
              ? this.dateFormatter(this.map.updated_at)
              : "",
            varName: "updateDate",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(4, 0, {
            icon: "person",
            label: this.$i18n.t("CreatedBy"),
            value: this.getUserName(this.map.user_id),
            varName: "user_id",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(5, 0, {
            icon: "person",
            label: this.$i18n.t("UpdatedBy"),
            value: this.getUserName(this.map.updated_by),
            varName: "updated_by",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
        }

        if (this.editingData) {
          fields.splice(0, 0, {
            label: this.$i18n.tc("Label", 1),
            value: this.map.code,
            varName: "code",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(1, 0, {
            icon: "mdi-format-list-bulleted",
            label: this.$i18n.t("Index"),
            value: this.map.index,
            varName: "index",
            inputType: "int_pos",
            min: 0,
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(2, 0, {
            label: this.$i18n.t("URL"),
            value: this.map.url,
            varName: "url",
            inputType: "text",
            disabled: false,
            required: true,
            size: "100%",
          });
          fields.splice(3, 0, {
            icon: "mdi-power",
            label: this.$i18n.t("Active"),
            value: this.editingData
              ? this.map.active
                ? "True"
                : "False"
              : this.map.active
              ? this.$i18n.t("Yes")
              : this.$i18n.t("No"),
            varName: "active",
            inputType: "radio",
            disabled: false,
            required: false,
            radioOptions: [
              { value: "True", label: this.$i18n.t("Yes") },
              { value: "False", label: this.$i18n.t("No") },
            ],
            size: "50%",
          });
          fields.splice(4, 0, {
            label: this.$i18n.tc("Locked"),
            value: this.editingData
              ? this.map.locked
                ? "True"
                : "False"
              : this.map.locked
              ? this.$i18n.t("Yes")
              : this.$i18n.t("No"),
            varName: "locked",
            inputType: "radio",
            disabled: false,
            required: false,
            radioOptions: [
              { value: "True", label: this.$i18n.t("Yes") },
              { value: "False", label: this.$i18n.t("No") },
            ],
            size: "50%",
          });
        }
      }
      return fields;
    },

    cardObjectIdentifier() {
      if (this.map) {
        return this.map.code;
      }
      return "";
    },

    cardHeight() {
      return "100%";
    },
  },

  methods: {
    ...mapActions(["fetchConsumablesById"]),

    async getMapByID() {
      let id = this.selectedMap.id;
      this.loading = true;
      try {
        this.map = await this.fetchConsumablesById(id);

        this.originalMap = _.cloneDeep(this.map);
      } catch (error) {
        await this.$router.replace("/error");
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    updateMap(updatedValue) {
      this.map[updatedValue.varName] = updatedValue.value;
    },

    cancelEdit() {
      this.discardChangesDialogIsOpen = false;
      this.editingData = false;
      if (this.map.uuid) {
        this.map = _.cloneDeep(this.originalMap);
      } else {
        this.$emit("clear-selected-item");
      }
    },

    toggleEdit() {
      if (this.editingData) {
        this.discardChangesDialogIsOpen = true;
      } else {
        this.editingData = !this.editingData;
      }
    },
    async saveObject() {
      this.saveChangesDialogIsOpen = false;
      try {
        this.loading = true;

        this.createDataToSend();

        if (Object.keys(this.dataToSend).length > 0) {
          let data = this.dataToSend;
          if (!this.creatingMap) {
            data.id = this.map.id;
          }
          await this.$store.dispatch(this.actionToExecute, data);
        }

        this.editingData = false;

        if (!this.creatingMap) {
          this.getMapByID();
          this.$toasted.global.toast_success(
            this.$i18n.t("MapEditedSuccessfully")
          );
        } else {
          this.$toasted.global.toast_success(
            this.$i18n.t("MapCreatedSuccessfully")
          );
        }

        bus.$emit("reload-maps");
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loading = false;
        this.dataToSend = {};
      }
    },

    createDataToSend() {
      Object.keys(this.map).forEach((key) => {
        if (this.map[key] !== this.originalMap[key]) {
          this.dataToSend[key] = this.map[key];
        }
      });
    },

    handleError(error) {
      let errorHandle = utils.errorHandler(error);

      if (typeof errorHandle === "object") {
        if (errorHandle.hasOwnProperty("url")) {
          this.$toasted.global.toast_error(this.$i18n.t('InvalidURL'));
        }
        if (errorHandle.hasOwnProperty("code")) {
          this.$toasted.global.toast_error(this.$i18n.tc('Label',1)+" "+this.$i18n.t('validationunique'));
        }
      } else if (typeof errorHandle === "string") {
        this.$toasted.global.toast_error(errorHandle);
      }
    },

    setCreateMapVariables() {
      this.map = {
        code: "",
        index: null,
        active: false,
        locked: false,
        url: "",
        visible: false,
        type: "WMS",
        request_method: "GET",
      };
      this.originalMap = {};
      this.actionToExecute = "createConsumables";
      this.submitButtonLabel = "Create";
      this.cardLabel = "CreateMap";
      this.editingData = true;
    },

    setEditMapVariables() {
      this.getMapByID();
      this.actionToExecute = "editConsumables";
      this.submitButtonLabel = "Save";
      this.cardLabel = "EditMap";
    },

    clearVariables() {
      this.map = null;
      this.originalMap = null;
      this.editingData = false;
    },

    requiredFieldsValidation() {
      return this.requiredFieldsAreFilled;
    },

    dateFormatter(date) {
      const dateArray = date.split(" ");
      return dateArray[0] + " / " + dateArray[1];
    },
  },
};
</script>

<style scoped>
.map {
  height: 100%;
  max-height: 100%;
  background-color: #fff;
  position: relative;
  border-radius: 8px;
}

.map-card {
  height: 100%;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}

.no.map-card {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
}

.no.map-card > span {
  color: var(--app-primary-base);
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
}

.map-info-container {
  gap: 8px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/Maps/Map.vue